/*
 * 戒导航 · 统一审核中心 —— 前端「处置回执」模型
 *
 * 设计依据：doc/统一审核中心设计方案.md §5.14.5 / §5.14.9。
 *
 * ★ 为什么必须有这个文件（这是「通知内容空白」的根因修复）：
 *   通知 API 返回的关系是
 *       relationships.subject = { type: 'moderationReceipts', id: '1' }
 *   而 Flarum 前端的本地数据仓库 app.store **只接受已注册的 type**：
 *       vendor/flarum/core/js/src/common/Store.ts → pushObject()
 *       if (!this.models[data.type]) {
 *         setTimeout(() => { throw new Error(`Pushing object of type \`${data.type}\` not allowed...`); });
 *         return null;                     // ← 未注册：资源被静默丢弃，不进 store
 *       }
 *   接着 vendor/flarum/core/js/src/common/Model.ts → hasOne()
 *       if (relationshipData) return this.store.getById(relationshipData.type, relationshipData.id);
 *   getById 查不到（因为压根没进 store）⇒ 返回 undefined ⇒ notification.subject() === undefined
 *   ⇒ vendor/flarum/core/js/src/forum/components/NotificationList.js:82
 *       if (typeof subject === 'undefined') return;   // ← 整条通知被丢弃
 *   ⇒ 通知下拉面板只剩「通知」标题 + 全选/清空图标，内容一片空白。
 *
 *   所以：**新通知类型的 subject 是什么模型，就必须在前端注册同名 type 的模型类**。
 *
 * 属性与后端一一对应（只读，回执是「凭证」，客户端不允许改写）：
 *   JieDH\Review\Api\Resource\ModerationReceiptResource::fields()
 *   type 名 'moderationReceipts' 必须与 ModerationReceiptResource::type() 完全一致。
 */
import Model from 'flarum/common/Model';
/**
 * 处置回执。属性名与 API 的 attributes 同名（驼峰），由该资源直接输出。
 */
export default class ModerationReceipt extends Model {
  /**
   * 处置动作：hide / delete / unapprove / ignore_flags / warn / approve。
   * 与后端 ModerationReceipt::ACTION_* 常量、前端 DisposeReasonModal 取值三方一致。
   */
  action() {
    return Model.attribute('action').call(this);
  }

  /**
   * 处置原因编码（可选）：delete 必填，其余动作可选。
   * 前端用它去取本地化文案 jiedh-review.forum.notifications.reason_<code>。
   */
  reasonCode() {
    return Model.attribute('reasonCode').call(this);
  }

  /**
   * 管理员手填的原因原文（可选）。有值时优先于 reasonCode 展示。
   */
  reasonText() {
    return Model.attribute('reasonText').call(this);
  }

  /** 被处置的帖子 ID（帖子已删除时该 ID 仅作留痕）。 */
  postId() {
    return Model.attribute('postId').call(this);
  }

  /** 所属主题 ID —— 通知点击后跳回该主题。 */
  discussionId() {
    return Model.attribute('discussionId').call(this);
  }

  /** 帖子在主题内的楼层号（用于生成 /d/{id}/{number} 深链）。 */
  postNumber() {
    return Model.attribute('postNumber').call(this);
  }

  /**
   * 主题标题的**快照**。刻意存快照而非关联查询：
   * 帖子/主题被删除后回执仍要能说清「你的哪篇内容被处置了」。
   */
  discussionTitle() {
    return Model.attribute('discussionTitle').call(this);
  }

  /** 收件人角色：author / reporter / subject_user。 */
  recipientRole() {
    return Model.attribute('recipientRole').call(this);
  }

  /** 回执生成时间。 */
  createdAt() {
    return Model.attribute('createdAt', value => value ? new Date(value) : null).call(this);
  }

  /**
   * 所属主题关系（只读）。
   *
   * ★ 为什么必须有：核心 NotificationList.js:87-93 用
   *     if (subject instanceof Discussion) discussion = subject;
   *     else if (subject && subject.discussion) discussion = subject.discussion();
   *   来决定通知落在哪个分组 key（discussion.id()）。
   *   本模型没有这个方法 ⇒ 抛错/取不到 ⇒ 通知归入「中立分组」（标题显示论坛名）。
   *
   * 后端 ModerationReceiptResource 已暴露 includable 的 ToOne('discussion')，
   * 通知接口的默认 include 会带上 subject.discussion，因此这里能拿到数据。
   *
   * 返回 false（而非 undefined）与 Flarum 其它模型的 hasOne 惯例一致：
   * 主题被硬删除时关系为空 ⇒ 前端回退中立分组，通知正文仍有标题快照。
   */
  discussion() {
    return Model.hasOne('discussion').call(this);
  }
}
flarum.reg.add('jiedh-review', 'forum/models/ModerationReceipt', ModerationReceipt);